import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	IconButton,
	Button,
	TextField,
	InputAdornment,
	Chip,
	Grid,
	Switch,
	Tooltip,
	Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import BusinessIcon from "@material-ui/icons/Business";
import CheckCircleOutlineOutlinedIcon from "@material-ui/icons/CheckCircleOutlineOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

import CompanyModal from "./CompanyModal";
import CompanyDetailsDrawer from "./CompanyDetailsDrawer";
import toastCompanyError from "./companyErrors";

const useStyles = makeStyles((theme) => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	statsRow: {
		marginBottom: theme.spacing(1),
	},
	statCard: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		gap: 14,
		border: `1px solid ${theme.custom.border}`,
		backgroundColor: theme.custom.surface,
		borderRadius: 12,
	},
	statIcon: {
		width: 44,
		height: 44,
		borderRadius: 12,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
	},
	statLabel: {
		fontSize: "0.75rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		color: theme.custom.textSecondary,
	},
	statValue: {
		fontWeight: 800,
		fontSize: "1.5rem",
		lineHeight: 1.2,
		letterSpacing: "-0.02em",
	},
	companyCell: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		fontWeight: 600,
	},
	companyIcon: {
		width: 34,
		height: 34,
		borderRadius: 10,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.custom.sidebarActiveBg,
		color: theme.palette.primary.main,
		flexShrink: 0,
	},
	statusCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 4,
	},
	statusChipActive: {
		backgroundColor: "rgba(5,150,105,.12)",
		color: "#059669",
		fontWeight: 600,
	},
	statusChipInactive: {
		backgroundColor: theme.custom.surfaceHover,
		color: theme.custom.textSecondary,
		fontWeight: 600,
	},
	limitDim: {
		color: theme.custom.textSecondary,
	},
	emptyBox: {
		padding: theme.spacing(4),
		textAlign: "center",
		color: theme.custom.textSecondary,
	},
}));

const STAT_COLORS = {
	total: { bg: "rgba(5,150,105,.12)", fg: "#059669" },
	active: { bg: "rgba(14,165,233,.12)", fg: "#0EA5E9" },
	users: { bg: "rgba(245,158,11,.12)", fg: "#F59E0B" },
};

const StatCard = ({ label, value, icon, colorKey }) => {
	const classes = useStyles();
	const colors = STAT_COLORS[colorKey];
	return (
		<Paper className={classes.statCard} elevation={0}>
			<div
				className={classes.statIcon}
				style={{ backgroundColor: colors.bg, color: colors.fg }}
			>
				{icon}
			</div>
			<div>
				<Typography className={classes.statLabel}>{label}</Typography>
				<Typography className={classes.statValue}>{value}</Typography>
			</div>
		</Paper>
	);
};

const isCompanyActive = (company) => company.status !== false;

const companyUsersCount = (company) =>
	company.usersCount ?? company.users?.length ?? 0;

const LimitCell = ({ count, max }) => {
	const classes = useStyles();
	if (!max) return <>{count}</>;
	return (
		<>
			{count}
			<span className={classes.limitDim}>/{max}</span>
		</>
	);
};

const Companies = () => {
	const classes = useStyles();

	const [companies, setCompanies] = useState([]);
	const [count, setCount] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [refreshKey, setRefreshKey] = useState(0);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [deletingCompany, setDeletingCompany] = useState(null);
	// Empresa aberta no painel lateral de detalhes
	const [detailsCompany, setDetailsCompany] = useState(null);

	useEffect(() => {
		setPageNumber(1);
	}, [searchParam, refreshKey]);

	useEffect(() => {
		let active = true;
		setLoading(true);
		const delayDebounce = setTimeout(async () => {
			try {
				const { data } = await api.get("/companies", {
					params: { searchParam, pageNumber },
				});
				if (!active) return;
				setCompanies((prev) => {
					if (pageNumber === 1) return data.companies;
					const merged = [...prev];
					data.companies.forEach((company) => {
						const index = merged.findIndex((c) => c.id === company.id);
						if (index !== -1) merged[index] = company;
						else merged.push(company);
					});
					return merged;
				});
				setHasMore(Boolean(data.hasMore));
				setCount(data.count ?? data.companies.length);
			} catch (err) {
				if (active) toastError(err);
			}
			if (active) setLoading(false);
		}, 400);
		return () => {
			active = false;
			clearTimeout(delayDebounce);
		};
	}, [searchParam, pageNumber, refreshKey]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("company", (data) => {
			if (data.action === "create" || data.action === "update") {
				setCompanies((prev) => {
					const index = prev.findIndex((c) => c.id === data.company.id);
					if (index !== -1) {
						const next = [...prev];
						next[index] = { ...next[index], ...data.company };
						return next;
					}
					if (data.action !== "create") return prev;
					return [...prev, data.company].sort((a, b) =>
						a.name.localeCompare(b.name)
					);
				});
				if (data.action === "create") {
					setCount((prev) => prev + 1);
				}
			}

			if (data.action === "delete") {
				setCompanies((prev) =>
					prev.filter((c) => c.id !== +data.companyId)
				);
				setCount((prev) => Math.max(0, prev - 1));
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleRefresh = () => setRefreshKey((prev) => prev + 1);

	const handleScroll = (e) => {
		if (!hasMore || loading) return;
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			setPageNumber((prev) => prev + 1);
		}
	};

	const handleToggleStatus = async (company) => {
		const newStatus = !isCompanyActive(company);
		try {
			await api.put(`/companies/${company.id}`, { status: newStatus });
			setCompanies((prev) =>
				prev.map((c) =>
					c.id === company.id ? { ...c, status: newStatus } : c
				)
			);
			toast.success(
				i18n.t(
					newStatus
						? "companies.toasts.activated"
						: "companies.toasts.deactivated"
				)
			);
		} catch (err) {
			toastCompanyError(err);
		}
	};

	const handleDelete = async () => {
		try {
			await api.delete(`/companies/${deletingCompany.id}`);
			toast.success(i18n.t("companies.toasts.deleted"));
			handleRefresh();
		} catch (err) {
			toastCompanyError(err);
		}
		setDeletingCompany(null);
	};

	const totalCompanies = count || companies.length;
	const activeCompanies = companies.filter(isCompanyActive).length;
	const totalUsers = companies.reduce(
		(acc, company) => acc + companyUsersCount(company),
		0
	);

	return (
		<MainContainer>
			<ConfirmationModal
				title={
					deletingCompany &&
					`${i18n.t("companies.confirmationModal.deleteTitle")} ${
						deletingCompany.name
					}?`
				}
				open={Boolean(deletingCompany)}
				onClose={() => setDeletingCompany(null)}
				onConfirm={handleDelete}
			>
				{i18n.t("companies.confirmationModal.deleteMessage")}
			</ConfirmationModal>
			<CompanyModal
				open={modalOpen}
				onClose={() => {
					setModalOpen(false);
					setSelectedCompany(null);
				}}
				company={selectedCompany}
				onSaved={handleRefresh}
			/>
			<CompanyDetailsDrawer
				open={Boolean(detailsCompany)}
				company={detailsCompany}
				onClose={() => setDetailsCompany(null)}
				onEdit={(company) => {
					setDetailsCompany(null);
					setSelectedCompany(company);
					setModalOpen(true);
				}}
			/>
			<MainHeader>
				<Title>{i18n.t("companies.title")}</Title>
				<MainHeaderButtonsWrapper>
					<TextField
						placeholder={i18n.t("companies.searchPlaceholder")}
						type="search"
						value={searchParam}
						onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon style={{ color: "gray" }} />
								</InputAdornment>
							),
						}}
					/>
					<Tooltip title={i18n.t("companies.buttons.refresh")}>
						<IconButton size="small" onClick={handleRefresh}>
							<RefreshIcon />
						</IconButton>
					</Tooltip>
					<Button
						variant="contained"
						color="primary"
						onClick={() => setModalOpen(true)}
					>
						{i18n.t("companies.buttons.add")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Grid container spacing={2} className={classes.statsRow}>
				<Grid item xs={12} sm={4}>
					<StatCard
						label={i18n.t("companies.stats.total")}
						value={totalCompanies}
						icon={<BusinessIcon />}
						colorKey="total"
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<StatCard
						label={i18n.t("companies.stats.active")}
						value={activeCompanies}
						icon={<CheckCircleOutlineOutlinedIcon />}
						colorKey="active"
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<StatCard
						label={i18n.t("companies.stats.users")}
						value={totalUsers}
						icon={<PeopleAltOutlinedIcon />}
						colorKey="users"
					/>
				</Grid>
			</Grid>
			<Paper
				className={classes.mainPaper}
				variant="outlined"
				onScroll={handleScroll}
			>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>{i18n.t("companies.table.name")}</TableCell>
							<TableCell align="center">
								{i18n.t("companies.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("companies.table.users")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("companies.table.connections")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("companies.table.createdAt")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("companies.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading && companies.length === 0 ? (
							<TableRowSkeleton columns={6} />
						) : (
							<>
								{companies.map((company) => {
									const active = isCompanyActive(company);
									const isDefault = company.id === 1;
									return (
										<TableRow
											key={company.id}
											hover
											style={{ cursor: "pointer" }}
											onClick={() => setDetailsCompany(company)}
										>
											<TableCell>
												<div className={classes.companyCell}>
													<div className={classes.companyIcon}>
														<BusinessIcon fontSize="small" />
													</div>
													{company.name}
													{isDefault && (
														<Chip
															size="small"
															label={i18n.t("companies.table.default")}
														/>
													)}
												</div>
											</TableCell>
											<TableCell align="center">
												<div className={classes.statusCell}>
													<Chip
														size="small"
														label={i18n.t(
															active
																? "companies.table.active"
																: "companies.table.inactive"
														)}
														className={
															active
																? classes.statusChipActive
																: classes.statusChipInactive
														}
													/>
													<Tooltip
														title={
															isDefault
																? i18n.t(
																		"companies.table.cannotDeactivateDefault"
																  )
																: ""
														}
													>
														{/* stopPropagation: a linha inteira abre o painel
														    de detalhes, e alternar o status nao pode
														    abrir junto. */}
														<span onClick={(e) => e.stopPropagation()}>
															<Switch
																size="small"
																color="primary"
																checked={active}
																disabled={isDefault}
																onChange={() => handleToggleStatus(company)}
															/>
														</span>
													</Tooltip>
												</div>
											</TableCell>
											<TableCell align="center">
												<LimitCell
													count={companyUsersCount(company)}
													max={company.maxUsers}
												/>
											</TableCell>
											<TableCell align="center">
												<LimitCell
													count={company.connectionsCount ?? 0}
													max={company.maxConnections}
												/>
											</TableCell>
											<TableCell align="center">
												{new Date(company.createdAt).toLocaleDateString(
													"pt-BR"
												)}
											</TableCell>
											<TableCell
												align="center"
												onClick={(e) => e.stopPropagation()}
											>
												<IconButton
													size="small"
													onClick={() => {
														setSelectedCompany(company);
														setModalOpen(true);
													}}
												>
													<EditIcon />
												</IconButton>
												<Tooltip
													title={
														isDefault
															? i18n.t("companies.table.cannotDeleteDefault")
															: ""
													}
												>
													<span>
														<IconButton
															size="small"
															disabled={isDefault}
															onClick={() => setDeletingCompany(company)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</span>
												</Tooltip>
											</TableCell>
										</TableRow>
									);
								})}
								{!loading && companies.length === 0 && (
									<TableRow>
										<TableCell colSpan={6}>
											<div className={classes.emptyBox}>
												{i18n.t("companies.table.empty")}
											</div>
										</TableCell>
									</TableRow>
								)}
								{loading && <TableRowSkeleton columns={6} />}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Companies;
